import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Import cylinder images
import oxygenCylinder from '../../assets/cylinders/oxygen.png';
import argonCylinder from '../../assets/cylinders/argon.png';
import nitrogenCylinder from '../../assets/cylinders/nitrogen.png';
import co2Cylinder from '../../assets/cylinders/co2.png';
import acetyleneCylinder from '../../assets/cylinders/acetylene.png';
import heliumCylinder from '../../assets/cylinders/helium.png';
import hydrogenCylinder from '../../assets/cylinders/hydrogen.png';

// Import application icons
import weldingIcon from '../../assets/applications/welding.png';
import medicalIcon from '../../assets/applications/medical.png';
import laboratoryIcon from '../../assets/applications/laboratory.png';
import industrialIcon from '../../assets/applications/industrial.png';
import safetyIcon from '../../assets/applications/safety.png';

const GasCylinders = () => {
  const gasCylinderTypes = [
    {
      title: "Oxygen Gas Cylinder",
      description: "An Oxygen Gas Cylinder is a high-pressure container used to store and transport compressed or liquid oxygen for industrial, medical, and welding applications. It is widely used in fabrication, manufacturing, healthcare, metallurgy, and aerospace industries.",
      color: "text-blue-600",
      bgColor: "from-blue-500/10 to-blue-500/5",
      icon: "💨",
      image: oxygenCylinder,
      features: [
        { text: "High-pressure storage capability", icon: "🔐" },
        { text: "Medical and industrial grade options", icon: "🏥" },
        { text: "Various sizes available", icon: "📏" },
        { text: "Compliant with safety standards", icon: "✅" },
        { text: "Durable construction", icon: "🛠️" }
      ],
      applications: [
        { text: "Welding and cutting", icon: "⚡" },
        { text: "Medical respiratory support", icon: "🫁" },
        { text: "Metal fabrication", icon: "🔨" },
        { text: "Aerospace industry", icon: "🛩️" },
        { text: "Laboratory use", icon: "🧪" }
      ],
      appIcons: [weldingIcon, medicalIcon, industrialIcon]
    },
    {
      title: "Argon Gas Cylinder",
      description: "An argon gas cylinder is used to store and transport argon gas, which is commonly used in welding, metal fabrication, electronics manufacturing, and laboratory applications.",
      color: "text-purple-600",
      bgColor: "from-purple-500/10 to-purple-500/5",
      icon: "🛢️",
      image: argonCylinder,
      features: [
        { text: "Inert gas storage", icon: "🛡️" },
        { text: "High purity options", icon: "✨" },
        { text: "Multiple size configurations", icon: "📐" },
        { text: "Pressure regulation system", icon: "🎛️" },
        { text: "Certified for industrial use", icon: "📜" }
      ],
      applications: [
        { text: "TIG welding", icon: "⚡" },
        { text: "Plasma cutting", icon: "✂️" },
        { text: "Electronics manufacturing", icon: "🔌" },
        { text: "Light bulb production", icon: "💡" },
        { text: "Metal heat treatment", icon: "🔥" }
      ],
      appIcons: [weldingIcon, industrialIcon, laboratoryIcon]
    },
    {
      title: "Nitrogen Gas Cylinder",
      description: "A nitrogen gas cylinder is used to store and transport nitrogen gas, which is widely used in industrial, medical, and laboratory applications.",
      color: "text-green-600",
      bgColor: "from-green-500/10 to-green-500/5",
      icon: "🧪",
      image: nitrogenCylinder,
      features: [
        { text: "High-pressure storage", icon: "🔐" },
        { text: "Various purity levels", icon: "📊" },
        { text: "Temperature controlled options", icon: "🌡️" },
        { text: "Safety relief systems", icon: "🛟" },
        { text: "Industrial-grade construction", icon: "🏗️" }
      ],
      applications: [
        { text: "Food packaging", icon: "🍱" },
        { text: "Chemical processing", icon: "⚗️" },
        { text: "Tire inflation", icon: "🚗" },
        { text: "Laboratory work", icon: "🔬" },
        { text: "Electronics manufacturing", icon: "🔧" }
      ],
      appIcons: [industrialIcon, laboratoryIcon, medicalIcon]
    },
    {
      title: "CO₂ Gas Cylinder",
      description: "A CO₂ (carbon dioxide) gas cylinder is used to store and transport carbon dioxide, which is widely used in welding, food and beverage industries, medical applications, and industrial processes.",
      color: "text-gray-600",
      bgColor: "from-gray-500/10 to-gray-500/5",
      icon: "💭",
      image: co2Cylinder,
      features: [
        { text: "Pressure-regulated storage", icon: "🎚️" },
        { text: "Food-grade options", icon: "🍽️" },
        { text: "Various capacities", icon: "📏" },
        { text: "Temperature monitoring", icon: "🌡️" },
        { text: "Safety valve systems", icon: "🔒" }
      ],
      applications: [
        { text: "MIG welding", icon: "⚡" },
        { text: "Beverage carbonation", icon: "🥤" },
        { text: "Greenhouse enrichment", icon: "🌱" },
        { text: "Fire extinguishers", icon: "🧯" },
        { text: "Medical procedures", icon: "⚕️" }
      ],
      appIcons: [weldingIcon, industrialIcon, medicalIcon]
    },
    {
      title: "Dissolved Acetylene Gas Cylinder",
      description: "A Dissolved Acetylene (DA) gas cylinder is used to store and transport acetylene gas, which is widely used in welding, metal cutting, and other industrial applications.",
      color: "text-red-600",
      bgColor: "from-red-500/10 to-red-500/5",
      icon: "🔥",
      image: acetyleneCylinder,
      features: [
        { text: "Special porous filler", icon: "🧱" },
        { text: "Acetone dissolution system", icon: "💧" },
        { text: "Multiple safety features", icon: "🛡️" },
        { text: "Pressure monitoring", icon: "📊" },
        { text: "Industrial certification", icon: "✅" }
      ],
      applications: [
        { text: "Oxy-acetylene welding", icon: "🔥" },
        { text: "Metal cutting", icon: "✂️" },
        { text: "Brazing operations", icon: "🔨" },
        { text: "Heat treatment", icon: "🌡️" },
        { text: "Chemical synthesis", icon: "⚗️" }
      ],
      appIcons: [weldingIcon, industrialIcon, laboratoryIcon]
    },
    {
      title: "Helium Gas Cylinder",
      description: "Helium gas is a colorless, odorless, and non-flammable noble gas widely used in various industries. It is stored and transported in high-pressure cylinders.",
      color: "text-yellow-600",
      bgColor: "from-yellow-500/10 to-yellow-500/5",
      icon: "🎈",
      image: heliumCylinder,
      features: [
        { text: "Ultra-high purity options", icon: "✨" },
        { text: "Cryogenic capabilities", icon: "❄️" },
        { text: "Pressure regulation", icon: "🎛️" },
        { text: "Leak detection systems", icon: "🔍" },
        { text: "Multiple size options", icon: "📏" }
      ],
      applications: [
        { text: "MRI cooling", icon: "🏥" },
        { text: "Balloon inflation", icon: "🎈" },
        { text: "Leak detection", icon: "🔍" },
        { text: "Diving mixtures", icon: "🤿" },
        { text: "Scientific research", icon: "🔬" }
      ],
      appIcons: [medicalIcon, laboratoryIcon, industrialIcon]
    },
    {
      title: "Hydrogen Gas Cylinder",
      description: "Hydrogen gas (H₂) is a highly flammable, colorless, and odorless gas widely used in industries such as welding, chemical processing, and energy production. It is stored and transported in high-pressure cylinders.",
      color: "text-indigo-600",
      bgColor: "from-indigo-500/10 to-indigo-500/5",
      icon: "⚡",
      image: hydrogenCylinder,
      features: [
        { text: "High-pressure storage", icon: "🔐" },
        { text: "Special safety features", icon: "🛡️" },
        { text: "Purity monitoring", icon: "📊" },
        { text: "Temperature control", icon: "🌡️" },
        { text: "Certified construction", icon: "✅" }
      ],
      applications: [
        { text: "Fuel cells", icon: "🔋" },
        { text: "Chemical processing", icon: "⚗️" },
        { text: "Metal treatment", icon: "🔨" },
        { text: "Laboratory research", icon: "🔬" },
        { text: "Energy production", icon: "⚡" }
      ],
      appIcons: [industrialIcon, laboratoryIcon, weldingIcon]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-[160px]">
      {/* Enhanced Hero Section with 3D Cylinder Animation */}
      <section className="relative py-40 bg-gradient-to-b from-gray-900 via-[#1a1a1a] to-[#333333] overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:100px_100px]" />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800/40 via-gray-700/30 to-gray-800/40" />
        
        {/* 3D Rotating Cylinders */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="container mx-auto h-full relative">
            <motion.div
              animate={{ 
                rotateY: 360,
                transition: { duration: 25, repeat: Infinity, ease: "linear" }
              }}
              className="absolute top-1/2 -right-32 transform -translate-y-1/2"
            >
              {/* <div className="relative w-[500px] h-[500px]">
                {[oxygenCylinder, argonCylinder, nitrogenCylinder].map((img, idx) => (
                  <motion.img
                    key={idx}
                    src={img}
                    alt="Gas Cylinder"
                    className="absolute top-0 left-0 w-full h-full object-contain opacity-80"
                    style={{
                      transform: `rotateY(${idx * 120}deg) translateZ(300px)`
                    }}
                  />
                ))}
              </div> */}
            </motion.div>
          </div>
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-6xl mx-auto">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-5xl md:text-6xl font-bold text-white mb-8 tracking-tight"
            >
              Industrial Gas Cylinders
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="text-xl text-white max-w-4xl mx-auto leading-relaxed"
            >
              High-quality gas cylinders for industrial and laboratory applications
            </motion.p>
            
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.5, delay: 0.5 }}
              className="mt-16 h-1 max-w-xl mx-auto bg-gradient-to-r from-transparent via-gray-400 to-transparent"
            />
          </div>
        </div>
      </section>

      {/* Enhanced Content Section */}
      <section className="py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16">
            {gasCylinderTypes.map((cylinder, idx) => (
              <motion.div
                key={cylinder.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: idx * 0.1 }}
                className="group bg-white rounded-[2.5rem] p-12 shadow-lg hover:shadow-xl border border-gray-200 transition-all duration-500 hover:-translate-y-2"
              >
                {/* Enhanced Header with Image */}
                <div className="mb-12 relative">
                  <div className="flex flex-col items-center gap-8">
                    <div className="relative">
                      <div className={`w-48 h-48 rounded-3xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-500`}>
                        <img 
                          src={cylinder.image} 
                          alt={cylinder.title}
                          className="w-40 h-40 object-contain drop-shadow-xl"
                        />
                      </div>
                      <motion.div
                        animate={{ 
                          scale: [1, 1.2, 1],
                          opacity: [0.5, 1, 0.5]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                        className="absolute -bottom-4 -right-4 w-full h-full bg-gradient-to-br from-gray-200/20 to-transparent rounded-full blur-2xl"
                      />
                    </div>
                    <div className="text-center">
                      <h2 className={`text-4xl font-bold text-black mb-4`}>
                        {cylinder.title}
                      </h2>
                      <div className="w-48 h-1.5 bg-gradient-to-r from-gray-300 to-gray-400 rounded-full transform origin-center group-hover:scale-x-110 transition-transform duration-500 mx-auto"></div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Features and Applications */}
                <div className="grid md:grid-cols-2 gap-12">
                  <div className="space-y-8">
                    <h3 className="text-2xl font-semibold text-black flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      Key Features
                    </h3>
                    <ul className="space-y-6">
                      {cylinder.features.map((feature, featureIdx) => (
                        <motion.li
                          key={featureIdx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: featureIdx * 0.1 }}
                          className="flex items-center gap-4 group"
                        >
                          <span className="text-3xl">{feature.icon}</span>
                          <span className="text-lg text-black group-hover:text-black transition-colors duration-300">{feature.text}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-8">
                    <h3 className="text-2xl font-semibold text-black flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      Applications
                    </h3>
                    <ul className="space-y-6">
                      {cylinder.applications.map((app, appIdx) => (
                        <motion.li
                          key={appIdx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: appIdx * 0.1 }}
                          className="flex items-center gap-4 group"
                        >
                          <span className="text-3xl">{app.icon}</span>
                          <span className="text-lg text-black group-hover:text-black transition-colors duration-300">{app.text}</span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* Application Icons */}
                    <div className="flex gap-6 mt-8">
                      {cylinder.appIcons.map((icon, iconIdx) => (
                        <motion.img
                          key={iconIdx}
                          src={icon}
                          alt="Application"
                          className="w-16 h-16 object-contain opacity-80 hover:opacity-100 transition-opacity duration-300"
                          whileHover={{ scale: 1.15 }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Safety Notice Section */}
      <section className="py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.05, 0.1, 0.05]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-0 right-0 w-[50rem] h-[50rem] bg-gray-200/10 rounded-full blur-3xl"
        />
        
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto bg-white rounded-[2.5rem] p-16 shadow-xl border border-gray-200 relative"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-gray-200/20 to-transparent rounded-bl-full transform translate-x-8 -translate-y-8" />
            
            <div className="flex items-center gap-8 mb-12">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-5xl font-bold text-black">
                Safety Information
              </h2>
            </div>
            
            <div className="space-y-8 text-black">
              <p className="text-2xl leading-relaxed">
                Gas cylinders contain pressurized gases and must be handled with proper care and attention to safety protocols.
                Always follow manufacturer guidelines and safety regulations when handling gas cylinders.
              </p>
              <ul className="grid md:grid-cols-2 gap-6">
                {[
                  "Always secure cylinders in an upright position",
                  "Store in well-ventilated areas away from heat sources",
                  "Use appropriate regulators and fittings",
                  "Regular inspection and maintenance is required",
                  "Handle with proper safety equipment"
                ].map((item, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    className="flex items-center gap-6 text-xl group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors duration-300">
                      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="group-hover:text-black transition-colors duration-300">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 -translate-x-1/4">
          <motion.img
            src={safetyIcon}
            alt="Safety"
            className="w-[600px] h-[600px] object-contain opacity-10"
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 5, 0]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>
      </section>

      {/* Enhanced Contact Section */}
      <section className="py-32 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <motion.div
          animate={{ 
            scale: [1.1, 1, 1.1],
            opacity: [0.05, 0.1, 0.05]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
          className="absolute bottom-0 left-0 w-[40rem] h-[40rem] bg-gray-200/10 rounded-full blur-3xl"
        />
        
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h2 className="text-5xl md:text-6xl font-bold text-black mb-10">
              Need Help Selecting the Right Gas Cylinder?
            </h2>
            <p className="text-2xl text-black mb-16 max-w-4xl mx-auto">
              Our experts are here to help you choose the perfect gas cylinder for your specific requirements
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center px-16 py-8 text-2xl font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
            >
              Contact Our Experts
              <motion.svg 
                className="w-8 h-8 ml-4 transition-transform duration-300 group-hover:translate-x-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </motion.svg>
            </Link>
          </motion.div>
        </div>
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2">
          <div className="grid grid-cols-2 gap-12">
            {[industrialIcon, laboratoryIcon, medicalIcon, weldingIcon].map((icon, idx) => (
              <motion.img
                key={idx}
                src={icon}
                alt="Industry Icon"
                className="w-32 h-32 object-contain opacity-15"
                animate={{
                  y: [0, 15, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: idx * 0.5
                }}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GasCylinders;